import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, eur, originUrl } from "@/lib/api";
import { toast } from "sonner";
import { Field, Section, PriceSummary } from "@/pages/ConvoyageBooking";

const VEHICLE_TYPES = ["Citadine", "Berline", "SUV", "Break", "Utilitaire", "7 places", "Autre"];

const FORMULAS = [
  { id: "express", label: "🚗 Express — 25 €" },
  { id: "interieur", label: "✨ Intérieur — 45 €" },
  { id: "exterieur", label: "🚿 Extérieur — 35 €" },
  { id: "complete", label: "⭐ Complète — 70 €" },
  { id: "premium", label: "👑 Premium — 100 €" },
];

const OPTIONS = [
  { id: "pet_hair", label: "Poils d'animaux", price: 20 },
  { id: "seat_shampoo", label: "Shampoing d'un siège", price: 15 },
  { id: "carpet_deep", label: "Nettoyage moquette / coffre", price: 20 },
  { id: "deodorize", label: "Désodorisation", price: 10 },
  { id: "large_vehicle", label: "Utilitaire / 7 places", price: 20 },
];

export default function NettoyageBooking() {
  const [params] = useSearchParams();
  const preset = params.get("formule");
  const [form, setForm] = useState({
    formula: FORMULAS.find((f) => f.id === preset)?.id || "express",
    vehicle_type: "Citadine",
    brand: "",
    model: "",
    license_plate: "",
    address: "",
    scheduled_date: "",
    scheduled_time: "",
    options: [],
    notes: "",
    full_name: "",
    email: "",
    phone: "",
  });
  const [price, setPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    api
      .post(
        "/quotes/cleaning",
        { formula: form.formula, options: form.options },
        { signal: ctl.signal },
      )
      .then((r) => setPrice(r.data))
      .catch(() => {});
    return () => ctl.abort();
  }, [form.formula, form.options]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const toggleOption = (id) => {
    const s = new Set(form.options);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setForm({ ...form, options: Array.from(s) });
  };

  const canPay =
    form.brand && form.model && form.address && form.scheduled_date &&
    form.scheduled_time && form.full_name && form.email && form.phone;

  const submit = async () => {
    if (!canPay) {
      toast.error("Complétez tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client: { full_name: form.full_name, email: form.email, phone: form.phone },
        cleaning: {
          formula: form.formula,
          vehicle_type: form.vehicle_type,
          brand: form.brand,
          model: form.model,
          license_plate: form.license_plate || null,
          address: { label: form.address },
          scheduled_datetime: `${form.scheduled_date}T${form.scheduled_time || "10:00"}`,
          options: form.options,
          notes: form.notes || null,
        },
        origin_url: originUrl(),
      };
      const { data } = await api.post("/bookings/cleaning", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      console.error(e);
      toast.error("Une erreur est survenue.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="nettoyage-page" className="min-h-screen bg-[#050505]">
      <SEO title="Réserver un nettoyage automobile" />
      <Header variant="nettoyage" />

      <div className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.5em] gold uppercase">Service #04</div>
        <h1 className="mt-3 font-serif text-white text-4xl sm:text-5xl">Nettoyage automobile</h1>
        <p className="mt-3 text-gray-400 max-w-2xl">
          Choisissez une formule, ajoutez vos options, indiquez le lieu et le
          créneau. Paiement à 100% à la réservation.
        </p>

        <div className="mt-10 grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="space-y-8">
            <Section title="Formule">
              <div className="grid sm:grid-cols-2 gap-3">
                {FORMULAS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`select-formula-${f.id}`}
                    onClick={() => setForm({ ...form, formula: f.id })}
                    className={`text-left p-4 border transition-all ${
                      form.formula === f.id
                        ? "border-[color:var(--gold)] bg-[color:var(--gold)]/[0.06] text-white"
                        : "border-white/10 bg-black text-gray-300 hover:border-white/30"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </Section>

            <Section title="Options">
              <div className="grid sm:grid-cols-2 gap-2">
                {OPTIONS.map((o) => {
                  const active = form.options.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      data-testid={`option-${o.id}`}
                      onClick={() => toggleOption(o.id)}
                      className={`flex items-center justify-between px-4 py-3 border transition-all text-sm ${
                        active
                          ? "border-[color:var(--gold)] bg-[color:var(--gold)]/[0.06] text-white"
                          : "border-white/10 bg-black text-gray-300 hover:border-white/30"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`w-4 h-4 border ${active ? "bg-[color:var(--gold)] border-[color:var(--gold)]" : "border-white/30"}`}
                        />
                        {o.label}
                      </span>
                      <span className="gold text-xs">+ {eur(o.price)}</span>
                    </button>
                  );
                })}
              </div>
            </Section>

            <Section title="Véhicule">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Type de véhicule">
                  <select data-testid="input-vehicle-type" value={form.vehicle_type} onChange={set("vehicle_type")} className="inp">
                    {VEHICLE_TYPES.map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Immatriculation (facultatif)">
                  <input data-testid="input-plate" value={form.license_plate} onChange={set("license_plate")} placeholder="AA-000-AA" className="inp" />
                </Field>
                <Field label="Marque *">
                  <input data-testid="input-brand" value={form.brand} onChange={set("brand")} placeholder="Peugeot, Audi..." className="inp" />
                </Field>
                <Field label="Modèle *">
                  <input data-testid="input-model" value={form.model} onChange={set("model")} placeholder="308, A4..." className="inp" />
                </Field>
              </div>
            </Section>

            <Section title="Intervention & contact">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Adresse d'intervention *">
                  <input data-testid="input-address" value={form.address} onChange={set("address")} placeholder="Rue, ville" className="inp" />
                </Field>
                <Field label="Date *">
                  <input data-testid="input-date" type="date" value={form.scheduled_date} onChange={set("scheduled_date")} className="inp" />
                </Field>
                <Field label="Heure *">
                  <input data-testid="input-time" type="time" value={form.scheduled_time} onChange={set("scheduled_time")} className="inp" />
                </Field>
                <Field label="Commentaires">
                  <input data-testid="input-notes" value={form.notes} onChange={set("notes")} placeholder="Instructions particulières" className="inp" />
                </Field>
                <Field label="Nom complet *">
                  <input data-testid="input-fullname" value={form.full_name} onChange={set("full_name")} className="inp" />
                </Field>
                <Field label="Email *">
                  <input data-testid="input-email" type="email" value={form.email} onChange={set("email")} className="inp" />
                </Field>
                <Field label="Téléphone *">
                  <input data-testid="input-phone" value={form.phone} onChange={set("phone")} className="inp" />
                </Field>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  data-testid="btn-pay-nettoyage"
                  disabled={!canPay || submitting}
                  onClick={submit}
                  className="btn-gold"
                >
                  {submitting ? "Redirection..." : "Payer & réserver"}
                </button>
              </div>
            </Section>
          </div>

          <PriceSummary price={price} title="Devis instantané" />
        </div>
      </div>

      <Footer />
      <style>{`.inp{ width:100%; background:#0a0a0a; border:1px solid rgba(255,255,255,.1); color:#fff; padding:12px 14px; border-radius:2px; font-family:inherit; transition:border-color .2s;}
      .inp:focus{outline:none; border-color:var(--gold);}`}</style>
    </div>
  );
}
